import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { generateAuditedClient } from "../audit/auditedClient";
import { isAuthorizationError } from "../audit/graphqlErrors";
import { bucketSlices, sliceShare, Slice } from "../audit/usageChart";
import { getUsageReport } from "../graphql/queries";
import {
  GetUsageReportInput,
  UsageReport,
  UsageReportEntry,
} from "../models/API";

/**
 * Usage Report Viewer (audit-usage-report spec, Requirements 1, 2, 3).
 *
 * A superuser-only surface, rendered as a tab inside `AuditLogViewer`, that
 * shows application usage as a pie chart over the pre-aggregated
 * `daily_usage_summary` rows via the `getUsageReport` query. Mirrors the
 * conventions of its sibling `AuditLogViewer` (shared audited Amplify client,
 * shared `isAuthorizationError` classifier, MUI layout); the server pipeline
 * resolver remains the authoritative access gate.
 *
 * Behavior:
 * - On open (and whenever the dimension or window selection changes while
 *   active), issue exactly one `getUsageReport` retrieval carrying the current
 *   selections; defaults are dimension `category` / 30 days (Req 1.3, 3.3,
 *   3.4).
 * - Exclusive selection controls offer exactly category/user/team/operation
 *   and 7/30/90 days, always holding one indicated selection; they stay
 *   interactive during loading and after generic errors, and changed
 *   selections are retained on error (Req 3.1, 3.2, 1.7, 3.7).
 * - A retrieval in progress shows a loading indicator (Req 1.4); a retrieval
 *   that does not complete within 30 seconds is treated as failed (Req 1.9).
 * - Completions (success or failure) are applied only when they belong to the
 *   latest issued request, so a stale response never replaces the display for
 *   the current selections (Req 3.5, 3.6).
 * - Authorization error -> only an access-denied message (no chart, no
 *   controls) (Req 1.6). Other errors -> retrieval-failed message in place of
 *   the chart, controls kept for retry (Req 1.7).
 * - `data_as_of` present -> "Data as of YYYY-MM-DD (UTC)" caption (Req 1.5);
 *   null -> "no aggregated usage data yet" indication in its place (Req 1.8);
 *   zero entries -> empty-state message without a chart (Req 2.6).
 * - Otherwise a Recharts pie over `bucketSlices(entries)` (top-10 + "Other")
 *   with counts as slice values so sizing is proportional, plus a legend and
 *   tooltip showing each slice's label, count, and one-decimal percentage
 *   share (Req 2.3, 2.4).
 */

/** Selectable slicing dimensions (Req 3.1). */
type UsageDimension = "category" | "user" | "team" | "operation";

/** Selectable reporting windows in days (Req 3.2). */
type WindowDays = 7 | 30 | 90;

/** Retrieval outcome driving the normal, loading, denied, and error UIs. */
type ViewerStatus = "ok" | "loading" | "auth-error" | "error";

/** The applied result of the latest matching retrieval. */
type ReportData = { dataAsOf: string | null; entries: UsageReportEntry[] };

/** Default selections applied when the viewer opens (Req 3.3). */
const DEFAULT_DIMENSION: UsageDimension = "category";
const DEFAULT_WINDOW_DAYS: WindowDays = 30;

/** Retrieval deadline; exceeding it is treated as a failure (Req 1.9). */
const REQUEST_TIMEOUT_MS = 30_000;

/**
 * Fixed palette: one color per individual slice (up to `MAX_SLICES = 10`)
 * plus one for the "Other" bucket, so a fully bucketed chart never repeats.
 */
const SLICE_COLORS = [
  "#1f77b4",
  "#ff7f0e",
  "#2ca02c",
  "#d62728",
  "#9467bd",
  "#8c564b",
  "#e377c2",
  "#7f7f7f",
  "#bcbd22",
  "#17becf",
  "#aec7e8",
];

/**
 * Reject when `promise` does not settle within `ms` milliseconds (Req 1.9).
 * The caller treats the timeout rejection as a generic retrieval failure. A
 * late settlement of the underlying request is inert: the returned promise has
 * already settled, and the request-id guard would ignore it anyway.
 */
const withTimeout = <T,>(promise: Promise<T>, ms: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () =>
        reject(new Error(`Usage report retrieval timed out after ${ms} ms`)),
      ms
    );
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (reason) => {
        clearTimeout(timer);
        reject(reason);
      }
    );
  });

/** "count (share%)" detail shown in the legend and tooltip (Req 2.4). */
const formatSliceDetail = (count: number, total: number): string =>
  `${count} (${sliceShare(count, total).toFixed(1)}%)`;

interface UsageReportViewerProps {
  /**
   * Whether the viewer is the active (visible) tab. While inactive no
   * retrievals are issued; each activation or selection change while active
   * triggers exactly one retrieval. Defaults to true.
   */
  isActive?: boolean;
}

export const UsageReportViewer: React.FC<UsageReportViewerProps> = ({
  isActive = true,
}) => {
  const client = useMemo(() => generateAuditedClient(), []);

  // Selections (Req 3.1, 3.2) with the opening defaults (Req 3.3).
  const [dimension, setDimension] = useState<UsageDimension>(DEFAULT_DIMENSION);
  const [windowDays, setWindowDays] = useState<WindowDays>(DEFAULT_WINDOW_DAYS);

  // Result + status. The first fetch starts on mount, hence initial "loading".
  const [status, setStatus] = useState<ViewerStatus>("loading");
  const [report, setReport] = useState<ReportData | null>(null);

  // Monotonic id of the most recently issued retrieval. A completion (success
  // OR failure) is applied only when its captured id still equals this value,
  // so responses for superseded selections or timed-out requests are ignored
  // (Req 3.5, 3.6, 1.9).
  const latestRequestId = useRef(0);

  // Exactly one retrieval per activation or selection change (Req 1.3, 3.4).
  useEffect(() => {
    if (!isActive) return;
    const requestId = latestRequestId.current + 1;
    latestRequestId.current = requestId;
    setStatus("loading");
    const input: GetUsageReportInput = {
      dimension,
      window_days: windowDays,
    };
    withTimeout(
      client.graphql({ query: getUsageReport, variables: { input } }),
      REQUEST_TIMEOUT_MS
    ).then(
      (result) => {
        if (requestId !== latestRequestId.current) return; // stale (Req 3.6)
        const usageReport: UsageReport | null =
          (result as any)?.data?.getUsageReport ?? null;
        setReport({
          dataAsOf: usageReport?.data_as_of ?? null,
          entries: usageReport?.entries ?? [],
        });
        setStatus("ok");
      },
      (err) => {
        if (requestId !== latestRequestId.current) return; // stale (Req 3.6)
        console.error("Failed to retrieve usage report:", err);
        // Selections are state, so the changed values stay indicated by the
        // controls for retry (Req 3.7).
        setReport(null);
        setStatus(isAuthorizationError(err) ? "auth-error" : "error");
      }
    );
  }, [isActive, dimension, windowDays, client]);

  /** Keep exactly one option selected: ignore MUI's deselection null. */
  const handleDimensionChange = (
    _event: React.MouseEvent<HTMLElement>,
    value: UsageDimension | null
  ) => {
    if (value !== null) setDimension(value);
  };

  const handleWindowChange = (
    _event: React.MouseEvent<HTMLElement>,
    value: WindowDays | null
  ) => {
    if (value !== null) setWindowDays(value);
  };

  if (status === "auth-error") {
    // Req 1.6: render ONLY the access-denied message; no chart, no controls.
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          You do not have access to the usage report.
        </Alert>
      </Box>
    );
  }

  const renderChart = (entries: UsageReportEntry[]) => {
    const slices: Slice[] = bucketSlices(entries);
    // Bucketing conserves the total, so shares are fractions of the retrieved
    // total operation count (Req 2.3, 2.5).
    const total = slices.reduce((sum, slice) => sum + slice.count, 0);
    return (
      <Paper sx={{ p: 2 }}>
        <ResponsiveContainer width="100%" height={380}>
          <PieChart>
            <Pie
              data={slices}
              dataKey="count"
              nameKey="label"
              cx="50%"
              cy="50%"
              outerRadius={130}
              isAnimationActive={false}
            >
              {slices.map((slice, index) => (
                <Cell
                  key={`${slice.label}-${index}`}
                  fill={SLICE_COLORS[index % SLICE_COLORS.length]}
                />
              ))}
            </Pie>
            {/* Req 2.4: label, count, and one-decimal share per slice. */}
            <Tooltip
              formatter={(
                value: number | string | Array<number | string>,
                name: number | string
              ) => [
                formatSliceDetail(
                  typeof value === "number" ? value : Number(value),
                  total
                ),
                String(name),
              ]}
            />
            <Legend
              formatter={(label: React.ReactNode, entry: any) => {
                const count = Number(
                  entry?.payload?.count ?? entry?.payload?.payload?.count ?? 0
                );
                return `${String(label)}: ${formatSliceDetail(count, total)}`;
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </Paper>
    );
  };

  const renderReport = () => {
    if (!report) return null;
    if (report.dataAsOf === null) {
      // Req 1.8: the no-data-yet indication replaces the Data_As_Of_Date;
      // controls above stay available.
      return (
        <Alert severity="info">
          No aggregated usage data is available yet.
        </Alert>
      );
    }
    return (
      <>
        {/* Req 1.5: the data-as-of date on the same screen as the report. */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Data as of {report.dataAsOf} (UTC)
        </Typography>
        {report.entries.length === 0 ? (
          // Req 2.6: empty state without a chart; controls stay available.
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No usage data matches the current selections.
          </Typography>
        ) : (
          renderChart(report.entries)
        )}
      </>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Selection controls (Req 3.1, 3.2): exclusive groups always holding
          one indicated selection; interactive during loading and after
          generic errors (Req 1.7, 3.7). */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction="row"
          spacing={3}
          flexWrap="wrap"
          useFlexGap
          alignItems="center"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Slice by
            </Typography>
            <ToggleButtonGroup
              value={dimension}
              exclusive
              size="small"
              onChange={handleDimensionChange}
              aria-label="Usage dimension"
            >
              <ToggleButton value="category">Category</ToggleButton>
              <ToggleButton value="user">User</ToggleButton>
              <ToggleButton value="team">Team</ToggleButton>
              <ToggleButton value="operation">Operation</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Window
            </Typography>
            <ToggleButtonGroup
              value={windowDays}
              exclusive
              size="small"
              onChange={handleWindowChange}
              aria-label="Reporting window"
            >
              <ToggleButton value={7}>7 days</ToggleButton>
              <ToggleButton value={30}>30 days</ToggleButton>
              <ToggleButton value={90}>90 days</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>
      </Paper>

      {status === "loading" ? (
        // Req 1.4: loading indicator while a retrieval is in progress.
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : status === "error" ? (
        // Req 1.7 / 1.9: retrieval failed (including timeout); the message
        // replaces the chart and the controls above remain for retry.
        <Alert severity="error">
          Failed to retrieve usage data. Change a selection to retry.
        </Alert>
      ) : (
        renderReport()
      )}
    </Box>
  );
};

export default UsageReportViewer;
